import { compact } from 'lodash/fp';
import { createSelector } from 'reselect';
import { TextKey, RouteName, FeatureFlag } from '@mews-commander/enums';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getFeatureFlagService } from '@mews-commander/legacy-app-instance';
import {
    isCurrentEnterpriseServiceProviderWithBookableService,
    areCurrentEnterpriseCalendarEventsEnabled,
    areCurrentEnterpriseCustomLinksEnabled,
} from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    canCurrentProfileManageServiceConfigurations,
    canCurrentProfileConfigureEnterprise,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getSimpleSearchResult } from './getSimpleSearchResult';
import { SearchResult } from './types';

export default createSelector(
    getCurrentEnterpriseId,
    canCurrentProfileConfigureEnterprise,
    canCurrentProfileManageServiceConfigurations,
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    isCurrentEnterpriseServiceProviderWithBookableService,
    areCurrentEnterpriseCustomLinksEnabled,
    areCurrentEnterpriseCalendarEventsEnabled,
    (
        enterpriseId: string,
        canConfigureEnterprise: boolean,
        canManageServiceConfiguration: boolean,
        isServiceProvider: boolean,
        linksEnabled: boolean,
        eventsEnabled: boolean
    ) => {
        if (!canConfigureEnterprise || !enterpriseId) {
            return [];
        }

        const params = { query: { OwnerId: enterpriseId } };

        const newRoutingRulesOnAddEnabled = getFeatureFlagService().hasFlag(FeatureFlag.newRoutingRulesOnAddEnabled);

        const serviceProviderResults = isServiceProvider
            ? [
                  getSimpleSearchResult({
                      routeName: RouteName.RoutingRuleIndex,
                      titleKey: TextKey.RoutingRulePlural,
                      params,
                  }),
                  newRoutingRulesOnAddEnabled
                      ? getSimpleSearchResult({
                            routeName: RouteName.RoutingRuleIndex,
                            titleKey: TextKey.BillingAutomationPlural,
                            params,
                        })
                      : null,
                  getSimpleSearchResult({
                      routeName: RouteName.AccountingCategoryIndex,
                      titleKey: TextKey.AccountingCategoryPlural,
                      params,
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.AccountingConfigurationDetail,
                      titleKey: TextKey.AccountingConfiguration,
                      params: { enterpriseId },
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.CurrencyAcceptanceIndex,
                      titleKey: TextKey.CurrencyAcceptancePlural,
                      params,
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.CashierIndex,
                      titleKey: TextKey.CashierPlural,
                      params,
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.CounterIndex,
                      titleKey: TextKey.CounterPlural,
                      params,
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.DepartmentIndex,
                      titleKey: TextKey.DepartmentPlural,
                      params,
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.ResourceIndex,
                      titleKey: TextKey.SpacePlural,
                      params,
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.OutletIndex,
                      titleKey: TextKey.OutletPlural,
                      params,
                  }),
                  getSimpleSearchResult({
                      routeName: RouteName.DeviceHubIndex,
                      titleKey: TextKey.DeviceHubPlural,
                      params,
                  }),
                  linksEnabled
                      ? getSimpleSearchResult({
                            routeName: RouteName.CustomLinkIndex,
                            titleKey: TextKey.CustomLinkPlural,
                            params,
                        })
                      : null,
                  eventsEnabled
                      ? getSimpleSearchResult({
                            routeName: RouteName.CalendarEventIndex,
                            titleKey: TextKey.CalendarEventPlural,
                            params,
                        })
                      : null,
              ]
            : [];

        return compact<SearchResult>([
            ...serviceProviderResults,
            canManageServiceConfiguration
                ? getSimpleSearchResult({
                      routeName: RouteName.MailTemplateIndex,
                      titleKey: TextKey.MailTemplatePlural,
                      params,
                  })
                : null,
        ]);
    }
);
